import { isUpcomingAppointment } from '@avanta/shared/utils/appointments';
import { useFormContext, useWatch } from 'react-hook-form';
import { useFetchAppointmentsHistory } from 'hooks/useFetchAppointmentsHistory';
import { useFetchPatientRecords } from 'hooks/useFetchPatientRecords';
import { AppointmentsReservePayload } from 'pages/AppointmentsReserve/constants';
import { type AppointmentsReserveFormData } from 'pages/AppointmentsReserve/types';
import { SICK_LEAVE_FIELD_KEY } from '../constants';
import { hasMatchingPatientRecord } from '../utils';

interface UseDataStepBannersResult {
  showNoScheduleBanner: boolean;
  showReopenRecordBanner: boolean;
  isLoading: boolean;
}

export const useDataStepBanners = (): UseDataStepBannersResult => {
  const { control } = useFormContext<AppointmentsReserveFormData>();
  const [patient, contractId, recordFieldValues] = useWatch({
    control,
    name: [
      AppointmentsReservePayload.patient,
      AppointmentsReservePayload.contractId,
      AppointmentsReservePayload.recordFields,
    ],
  });
  const patientId = patient?.id ?? '';
  const { data: appointments = [], isLoading: isLoadingAppointments } =
    useFetchAppointmentsHistory(patientId);
  const { data: patientRecords = [], isLoading: isLoadingPatientRecords } =
    useFetchPatientRecords(patientId);

  const showNoScheduleBanner =
    (patient?.bookingBlockedReasons?.length ?? 0) > 0 ||
    appointments.some(appointment => isUpcomingAppointment(appointment));

  const showReopenRecordBanner =
    !showNoScheduleBanner &&
    hasMatchingPatientRecord({
      patientRecords,
      contractId,
      sickLeaveDate: recordFieldValues?.[SICK_LEAVE_FIELD_KEY],
    });

  return {
    showNoScheduleBanner,
    showReopenRecordBanner,
    isLoading: isLoadingAppointments || isLoadingPatientRecords,
  };
};
